// IND-036: ventana «Fuera del reparto» de «Coste por 100 m»: productos terminados
// con su motivo y materias primas sin destino del periodo elegido.
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { esNumberFormat } from '../utils/numberFormat';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import { MONTH_NAMES, MONTH_SHORT, wasteOutCsv } from '../utils/wasteControl';
import type { WasteOutMaterial, WasteOutOfShare, WasteOutProduct } from '../utils/wasteControl';
import SortableHeaderCell, { TEN_ROWS_MAX_HEIGHT } from '../components/SortableHeaderCell';

const MUTED = '#94a3b8';
const HEADER_BG = '#151f30';
const metresFormat = esNumberFormat({ maximumFractionDigits: 0 });

type ProductKey = 'code' | 'metres' | 'reason';
const PRODUCT_VALUES: Record<ProductKey, (row: WasteOutProduct) => SortValue> = {
  code: (row) => row.code,
  metres: (row) => row.metres,
  reason: (row) => row.reason,
};

type MaterialKey = 'code' | 'problem' | 'months' | 'products';
const MATERIAL_VALUES: Record<MaterialKey, (row: WasteOutMaterial) => SortValue> = {
  code: (row) => row.code,
  problem: (row) => row.problem,
  months: (row) => row.months.length,
  products: (row) => row.product_count,
};

function saveCsv(text: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function IndustrialCostWasteOutOfShareDialog({
  open,
  onClose,
  out,
  fallbackMonths,
  year,
  month,
}: {
  open: boolean;
  onClose: () => void;
  out: WasteOutOfShare;
  fallbackMonths: number[];
  year: number;
  month: number | null;
}) {
  const period = month ? `${MONTH_NAMES[month - 1].toLowerCase()} de ${year}` : String(year);
  const title = out.products.length > 0 ? 'Fuera del reparto' : 'Avisos del reparto';
  const fallback = fallbackMonths.filter((item) => month === null || item === month);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{`${title} · ${period}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <ProductsBlock rows={out.products} />
          <MaterialsBlock rows={out.materials} />
          {fallback.length ? (
            <Typography sx={{ color: MUTED, fontSize: 12 }}>
              {`Sin destino de la hoja STP-MP de ${fallback.map((item) => MONTH_NAMES[item - 1].toLowerCase()).join(', ')}: sale del recuento más cercano.`}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => saveCsv(wasteOutCsv(out), `fuera-del-reparto-${month ? `${year}-${String(month).padStart(2, '0')}` : year}.csv`)}
          sx={{ textTransform: 'none' }}
        >
          Descargar CSV
        </Button>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 800 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ProductsBlock({ rows }: { rows: WasteOutProduct[] }) {
  const [search, setSearch] = useState('');
  const sort = useTableSort<ProductKey>('metres', 'desc');
  const sorted = useMemo(() => {
    const text = search.trim().toLowerCase();
    const found = text
      ? rows.filter((row) => `${row.code} ${row.name ?? ''}`.toLowerCase().includes(text))
      : rows;
    return sortRows(found, PRODUCT_VALUES[sort.key ?? 'metres'], sort.direction);
  }, [rows, search, sort.key, sort.direction]);
  return (
    <Box>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{`Productos terminados fuera del reparto (${rows.length})`}</Typography>
        <TextField
          size="small"
          placeholder="Buscar producto"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Stack>
      <Typography sx={{ color: MUTED, fontSize: 12, mb: 0.5 }}>Qué hacer: completa el escandallo en Datos de planta.</Typography>
      {rows.length === 0 ? (
        <Typography sx={{ color: MUTED, fontSize: 13 }}>Todos los productos fabricados entran en el reparto.</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: TEN_ROWS_MAX_HEIGHT }}>
          <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: 0.75, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                <SortableHeaderCell label="Producto" columnKey="code" sort={sort} firstDirection="asc" sx={{ bgcolor: HEADER_BG }} />
                <SortableHeaderCell label="Metros" columnKey="metres" sort={sort} align="right" sx={{ bgcolor: HEADER_BG }} />
                <SortableHeaderCell label="Motivo" columnKey="reason" sort={sort} firstDirection="asc" sx={{ bgcolor: HEADER_BG }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((row) => (
                <TableRow key={row.product_id}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 800, fontSize: 12 }}>{row.code}</Typography>
                    {row.name ? <Typography sx={{ color: MUTED, fontSize: 10.5 }}>{row.name}</Typography> : null}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{metresFormat.format(row.metres)}</TableCell>
                  <TableCell>{row.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function MaterialsBlock({ rows }: { rows: WasteOutMaterial[] }) {
  const sort = useTableSort<MaterialKey>('products', 'desc');
  const sorted = sortRows(rows, MATERIAL_VALUES[sort.key ?? 'products'], sort.direction);
  return (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{`Materias primas sin destino (${rows.length})`}</Typography>
      <Typography sx={{ color: MUTED, fontSize: 12, mb: 0.5 }}>Qué hacer: corrige el libro de recuento, da de alta el material o reactívalo.</Typography>
      {rows.length === 0 ? (
        <Typography sx={{ color: MUTED, fontSize: 13 }}>Ninguna en este periodo.</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: TEN_ROWS_MAX_HEIGHT }}>
          <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: 0.75, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                <SortableHeaderCell label="Código" columnKey="code" sort={sort} firstDirection="asc" sx={{ bgcolor: HEADER_BG }} />
                <SortableHeaderCell label="Qué pasa" columnKey="problem" sort={sort} firstDirection="asc" sx={{ bgcolor: HEADER_BG }} />
                <SortableHeaderCell label="Meses" columnKey="months" sort={sort} sx={{ bgcolor: HEADER_BG }} />
                <SortableHeaderCell label="Productos" columnKey="products" sort={sort} align="right" sx={{ bgcolor: HEADER_BG }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((row) => (
                <TableRow key={row.key}>
                  <TableCell sx={{ fontWeight: 800 }}>{row.code}</TableCell>
                  <TableCell>{row.problem}</TableCell>
                  <TableCell>{row.months.map((month) => MONTH_SHORT[month - 1]).join(', ')}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.product_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
